/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Button,
  HStack,
  Text,
  Badge,
  Box,
  Spinner,
  VStack,
  Icon,
  Flex,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { FiRefreshCw, FiDatabase, FiActivity } from "react-icons/fi";
import { useEffect, useRef, useState, useMemo } from "react";
import api from "utils/api";

// Log satırını parse etme fonksiyonu
const parseLogLine = (log = "", index = 0, plaka = "-", terminalTipi = "-", imei = null) => {
  if (!log) return null;

  // Log formatı genellikle: IP DATE TIME LEVEL MESSAGE şeklinde
  // Örnek: "148.795.752.119 2025-07-28 14:29:15 INFO: @L03686254904008384512..."
  
  const parts = log.trim().split(/\s+/);
  if (parts.length < 4) return null;

  // Rowno kaldırıldı - artık kullanılmıyor
  
  // Tarih ve saat parse etme
  //
  // ÖNEMLİ: Traccar ham log satırı genelde önce SUNUCUNUN satırı yazdığı zamanı taşır
  // (örn. "2026-04-02 23:01:07 INFO: ..." veya "IP 2026-04-02 23:01:07 INFO: ...").
  // Decode edilmiş pozisyon satırlarında ayrıca "time: ..." ile CİHAZIN bildirdiği
  // zaman vardır; ikisi farklı olabilir. TARİH sütununda tutarlılık ve dosya sırasıyla
  // uyum için önce satır başındaki sunucu zamanını kullanıyoruz. Cihaz zamanı DATA
  // metninde "time: ..." olarak görülmeye devam eder (GT06 / Teltonika / diğer).
  let formattedTarih = "-";

  const formatYmdAndHms = (dateStr, hour, minute) => {
    const [year, month, day] = dateStr.split("-");
    return `${day}.${month}.${year} ${hour}:${minute}`;
  };

  // 0. Satır başı: [opsiyonel IP] YYYY-MM-DD HH:MM:SS (Traccar logger zamanı)
  const leadingServerTs = log.match(
    /^(?:\d{1,3}(?:\.\d{1,3}){3}\s+)?(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2}):(\d{2})\b/
  );
  if (leadingServerTs) {
    const [, dateStr, hour, minute] = leadingServerTs;
    formattedTarih = formatYmdAndHms(dateStr, hour, minute);
  } else {
    // 1. "time: YYYY-MM-DD HH:MM:SS" (sunucu başlığı olmayan satırlar)
    const timePattern = log.match(/time:\s*(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
    if (timePattern) {
      const [, dateStr, hour, minute] = timePattern;
      formattedTarih = formatYmdAndHms(dateStr, hour, minute);
    } else {
      // 2. Satırda geçen ilk YYYY-MM-DD HH:MM:SS
      const dateTimeMatch = log.match(/(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
      if (dateTimeMatch) {
        const [, dateStr, hour, minute] = dateTimeMatch;
        formattedTarih = formatYmdAndHms(dateStr, hour, minute);
      } else {
        // 3. @L veya @C ile başlayan data içinde tarih/saat olabilir
        // Format: @L03E...1812251620... (YYMMDDHHMM formatında - UTC)
        const embeddedDateMatch = log.match(/@[LC][^@]*?(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
        if (embeddedDateMatch) {
          const [, year, month, day, hour, minute] = embeddedDateMatch;
          const fullYear = parseInt(year) < 50 ? `20${year}` : `19${year}`;
          const utcDate = new Date(`${fullYear}-${month}-${day}T${hour}:${minute}:00Z`);
          const localDate = new Date(utcDate.getTime() + 3 * 60 * 60 * 1000);

          const localDay = String(localDate.getUTCDate()).padStart(2, "0");
          const localMonth = String(localDate.getUTCMonth() + 1).padStart(2, "0");
          const localYear = localDate.getUTCFullYear();
          const localHour = String(localDate.getUTCHours()).padStart(2, "0");
          const localMinute = String(localDate.getUTCMinutes()).padStart(2, "0");

          formattedTarih = `${localDay}.${localMonth}.${localYear} ${localHour}:${localMinute}`;
        } else {
          // 4. Eski format: YYYY-MM-DD ve HH:MM ayrı ayrı
          const dateMatch = log.match(/(\d{4}-\d{2}-\d{2})/);
          const timeMatch = log.match(/(\d{2}):(\d{2})(?::\d{2})?/);

          if (dateMatch && timeMatch) {
            const dateStr = dateMatch[1];
            const [, hour, minute] = timeMatch;
            formattedTarih = formatYmdAndHms(dateStr, hour, minute);
          }
        }
      }
    }
  }

  // IMEI'yi protokole göre parse et ve göster
  let yazilimVer = "-"; // Artık IMEI gösterilecek
  let data = log;

  // IMEI'yi log'dan veya parametre olarak gelen imei'den al
  let extractedImei = imei;
  
  // Eğer imei parametresi yoksa, log'dan çıkarmaya çalış
  if (!extractedImei) {
    // XTAKIP formatı: @C;86307101891623612;M12;... (17 karakter IMEI)
    const xtakipMatch = log.match(/@C;(\d{17})/);
    if (xtakipMatch) {
      extractedImei = xtakipMatch[1];
    } else {
      // Teltonika/Armoli: IMEI genellikle log içinde geçiyor (15 karakter)
      // Önce 15 karakterlik IMEI ara
      const imei15Match = log.match(/(\d{15})(?![0-9])/);
      if (imei15Match) {
        extractedImei = imei15Match[1];
      } else {
        // 17 karakterlik IMEI ara (XTAKIP için)
        const imei17Match = log.match(/(\d{17})(?![0-9])/);
        if (imei17Match) {
          extractedImei = imei17Match[1];
        }
      }
    }
  }

  // Protokole göre IMEI uzunluğunu ayarla
  if (extractedImei) {
    const terminalLower = (terminalTipi || "").toLowerCase();
    if (terminalLower.includes("xtakip")) {
      // XTAKIP: 17 karakter
      yazilimVer = extractedImei.length >= 17 ? extractedImei.substring(0, 17) : extractedImei;
    } else if (terminalLower.includes("armoli") || terminalLower.includes("teltonika") || terminalLower.includes("gt06")) {
      // Armoli, Teltonika, GT06: 15 karakter
      yazilimVer = extractedImei.length >= 15 ? extractedImei.substring(0, 15) : extractedImei;
    } else {
      // Protokol bilinmiyorsa, IMEI'yi olduğu gibi göster
      yazilimVer = extractedImei;
    }
  }

  // Data kısmı - log'un tamamı veya payload kısmı
  // @L veya @C ile başlayan kısımları bul
  const dataMatch = log.match(/(@[LC].*?)(?:\s|$)/);
  if (dataMatch) {
    data = dataMatch[1];
  } else {
    // Eğer @ ile başlayan bir şey yoksa, log'un son kısmını al
    let dataStart = log;
    const infoIndex = log.indexOf('INFO:');
    const errorIndex = log.indexOf('ERROR:');
    const warnIndex = log.indexOf('WARN:');
    
    if (infoIndex !== -1) {
      dataStart = log.substring(infoIndex + 5);
    } else if (errorIndex !== -1) {
      dataStart = log.substring(errorIndex + 6);
    } else if (warnIndex !== -1) {
      dataStart = log.substring(warnIndex + 5);
    } else if (parts.length > 4) {
      dataStart = parts.slice(4).join(' ');
    }
    
    data = (typeof dataStart === 'string' ? dataStart.trim() : String(dataStart || log)) || log;
  }

  // Tarih ve saati ayır (format: "DD.MM.YYYY HH:MM")
  let tarihOnly = "-";
  let saatOnly = "-";
  if (formattedTarih && formattedTarih !== "-") {
    const parts = formattedTarih.split(" ");
    if (parts.length === 2) {
      tarihOnly = parts[0];
      saatOnly = parts[1];
    } else {
      tarihOnly = formattedTarih;
    }
  }

  return {
    plaka: plaka, // Plaka parametre olarak geliyor
    terminalTipi: terminalTipi, // Terminal tipi parametre olarak geliyor
    yazilimVer,
    data: data, // Tüm data gösteriliyor
    fullData: data, // Tam data için
    tarih: formattedTarih || "-",
    tarihOnly,
    saatOnly,
    rawLog: log,
  };
};

export { parseLogLine };

function GelenDataModal({ isOpen, onClose, imei, deviceData }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [limit, setLimit] = useState(50);
  const scrollRef = useRef(null);
  
  const tableBg = "white";
  const headerBg = "gray.50";
  const rowHoverBg = "gray.50";

  // Device bilgilerinden plaka ve terminal tipi al
  const plaka = deviceData?.plaka || "-";
  // Terminal tipi için protokol tipleri: teltonika, xtakip, armoli, gt06
  const terminalTipi = useMemo(() => {
    if (deviceData?.protocol) {
      const protocol = (deviceData.protocol || "").toLowerCase().trim();
      if (protocol.includes('teltonika') || protocol.includes('fmb')) {
        return 'Teltonika';
      }
      if (protocol.includes('xtakip') || protocol.includes('takip')) {
        return 'XTAKIP';
      }
      if (protocol.includes('armoli')) {
        return 'Armoli';
      }
      if (protocol.includes('gt06') || protocol.includes('gs900')) {
        return 'GT06';
      }
      if (protocol) {
        return protocol.charAt(0).toUpperCase() + protocol.slice(1);
      }
    }
    if (logs.length > 0) {
      const firstLog = logs[0].toLowerCase();
      if (firstLog.includes('teltonika') || firstLog.includes('fmb')) {
        return 'Teltonika';
      }
      if (firstLog.includes('xtakip') || firstLog.includes('takip')) {
        return 'XTAKIP';
      }
      if (firstLog.includes('armoli')) {
        return 'Armoli';
      }
      if (firstLog.includes('gt06') || firstLog.includes('gs900')) {
        return 'GT06';
      }
    }
    return "-";
  }, [deviceData?.protocol, logs]);

  // Logları parse edip tablo formatına çevir
  const parsedData = useMemo(() => {
    if (!Array.isArray(logs) || logs.length === 0) return [];
    
    return logs
      .map((log, index) => parseLogLine(log, index, plaka, terminalTipi, imei))
      .filter(item => item !== null)
      .reverse(); // En yeni üstte
  }, [logs, plaka, terminalTipi, imei]);

  // API'den log çekme fonksiyonu
  const fetchLogs = async (overrideLimit) => {
    if (!imei) return;
    const effectiveLimit = overrideLimit || limit;
    setLoading(true);
    try {
      const res = await api.get(`/api/rawlogs/${imei}?limit=${effectiveLimit}`);
      const result = await res.json();
      const logList = Array.isArray(result) ? result : result.logs || [];
      setLogs(logList);
      if (logList.length > 0) {
        const plaka = deviceData?.plaka || "-";
        // Terminal tipi belirleme
        let terminalTipi = "-";
        if (deviceData?.protocol) {
          const protocol = (deviceData.protocol || "").toLowerCase().trim();
          if (protocol.includes('teltonika') || protocol.includes('fmb')) {
            terminalTipi = 'Teltonika';
          } else if (protocol.includes('xtakip') || protocol.includes('takip')) {
            terminalTipi = 'XTAKIP';
          } else if (protocol.includes('armoli')) {
            terminalTipi = 'Armoli';
          } else if (protocol.includes('gt06') || protocol.includes('gs900')) {
            terminalTipi = 'GT06';
          } else if (protocol) {
            terminalTipi = protocol.charAt(0).toUpperCase() + protocol.slice(1);
          }
        }
        const firstParsed = parseLogLine(logList[logList.length - 1], 0, plaka, terminalTipi, imei);
        setLastUpdate(firstParsed?.tarih || null);
      } else {
        setLastUpdate(null);
      }
    } catch (err) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && imei) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imei, isOpen]);

  // Tablo render edildikten sonra en üstten başla
  useEffect(() => {
    if (isOpen && parsedData.length > 0 && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [parsedData, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" bg="white" p={1}>
        
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200" position="relative">
          <ModalCloseButton size="sm" position="absolute" top={2} right={2} />
          <HStack spacing={3} justify="space-between" align="center" pr={8}>
            <VStack spacing={1} align="start">
              <Text fontSize="lg" fontWeight="medium" color="gray.800">
                Gelen Data
              </Text>
              <HStack spacing={2}>
                <Text fontSize="xs" color="gray.600">
                  IMEI: {imei || "Bilinmiyor"}
                </Text>
                <Badge colorScheme="green" px={2} py={0.5} borderRadius="md" fontSize="xs">
                  {parsedData.length} kayıt
                </Badge>
              </HStack>
            </VStack>
            
            <HStack spacing={3} align="center">
              {lastUpdate && (
                <Text fontSize="xs" color="gray.500">
                  Son veri: {lastUpdate}
                </Text>
              )}
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Icon as={FiRefreshCw} />}
                onClick={() => fetchLogs()}
                isLoading={loading}
                loadingText="Yenileniyor"
              >
                Yenile
              </Button>
            </HStack>
          </HStack>
        </Box>

        <ModalBody px={6} py={4} maxH="500px" overflowY="auto">
          {loading ? (
            <VStack py={20} spacing={4}>
              <Box position="relative">
                <Spinner size="xl" color="blue.500" thickness="3px" />
                <Icon 
                  as={FiActivity} 
                  position="absolute" 
                  top="50%" 
                  left="50%" 
                  transform="translate(-50%, -50%)"
                  color="blue.500"
                  boxSize={6}
                />
              </Box>
              <VStack spacing={1}>
                <Text fontWeight="medium" color="gray.700">
                  Veriler Yükleniyor
                </Text>
                <Text fontSize="sm" color="gray.500">
                  IMEI {imei} için veri çekiliyor...
                </Text>
              </VStack>
            </VStack>
          ) : parsedData.length > 0 ? (
            <Box
              ref={scrollRef}
              bg={tableBg}
              borderRadius="lg"
              border="2px solid"
              borderColor="gray.300"
              overflow="auto"
              maxH="500px"
              shadow="md"
              sx={{
                "table": {
                  borderCollapse: "separate",
                  borderSpacing: "0 0",
                },
                "td, th": {
                  margin: 0,
                }
              }}
            >
              <Table variant="simple" size="sm">
                <Thead position="sticky" top={0} zIndex={10} bg={headerBg} borderBottom="1px solid" borderColor="gray.300">
                  <Tr>
                    <Th fontSize="10px" fontWeight="600" color="gray.700" borderColor="gray.300" py={1.5} px={3} borderBottom="1px solid" borderRight="1px solid">
                      Plaka
                    </Th>
                    <Th fontSize="10px" fontWeight="600" color="gray.700" borderColor="gray.300" py={1.5} px={3} borderBottom="1px solid" borderRight="1px solid">
                      Terminal Tipi
                    </Th>
                    <Th fontSize="10px" fontWeight="600" color="gray.700" borderColor="gray.300" w="150px" maxW="150px" py={1.5} px={3} borderBottom="1px solid" borderRight="1px solid">
                      IMEI
                    </Th>
                    <Th fontSize="10px" fontWeight="600" color="gray.700" borderColor="gray.300" minW="600px" maxW="800px" py={1.5} px={3} borderBottom="1px solid" borderRight="1px solid">
                      Data
                    </Th>
                    <Th fontSize="10px" fontWeight="600" color="gray.700" borderColor="gray.300" py={1.5} px={3} borderBottom="1px solid" w="180px" minW="180px">
                      Tarih
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {parsedData.map((item, index) => (
                    <Tr
                      key={index}
                      _hover={{ bg: rowHoverBg }}
                      transition="background 0.2s"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      <Td fontSize="9px" borderColor="gray.200" fontWeight="500" py={1} px={3} lineHeight="1.2" m={0} borderRight="1px solid">
                        {item.plaka}
                      </Td>
                      <Td fontSize="9px" borderColor="gray.200" py={1} px={3} lineHeight="1.2" m={0} borderRight="1px solid">
                        {item.terminalTipi}
                      </Td>
                      <Td fontSize="8px" borderColor="gray.200" fontFamily="monospace" w="150px" maxW="150px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={item.yazilimVer} py={1} px={3} lineHeight="1.2" m={0} borderRight="1px solid">
                        {item.yazilimVer}
                      </Td>
                      <Td 
                        fontSize="8px" 
                        borderColor="gray.200" 
                        fontFamily="monospace"
                        minW="600px"
                        maxW="800px"
                        wordBreak="break-all"
                        whiteSpace="pre-wrap"
                        title={item.fullData}
                        py={1}
                        px={3}
                        lineHeight="1.2"
                        m={0}
                        borderRight="1px solid"
                      >
                        {item.data}
                      </Td>
                      <Td fontSize="9px" borderColor="gray.200" py={1} px={3} lineHeight="1.2" m={0} w="180px" minW="180px">
                        <Text fontSize="8px" lineHeight="1.2" m={0} whiteSpace="nowrap">
                          {item.tarih || (item.tarihOnly && item.saatOnly ? `${item.tarihOnly} ${item.saatOnly}` : item.tarihOnly || item.saatOnly || "-")}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
              ) : (
                <VStack py={10} spacing={3}>
                  <Icon as={FiDatabase} boxSize={12} color="gray.600" />
                  <VStack spacing={1}>
                    <Text color="gray.400" fontSize="md" fontWeight="medium">
                      Veri Bulunamadı
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                      Bu IMEI için henüz log kaydı bulunmuyor
                    </Text>
                  </VStack>
                </VStack>
          )}
        </ModalBody>

        {/* Footer */}
        <Box bg="white" px={6} py={4} borderTop="1px solid" borderColor="gray.200">
          <Flex justify="space-between" align="center">
            <HStack spacing={4} fontSize="xs" color="gray.500">
              <Text>Toplam: {parsedData.length} kayıt</Text>
              <HStack spacing={2} align="center">
                <Text fontSize="xs" color="gray.600">Limit:</Text>
                <Select
                  variant="flushed"
                  size="sm"
                  value={limit}
                  onChange={(e) => {
                    const newLimit = Number(e.target.value);
                    setLimit(newLimit);
                    fetchLogs(newLimit);
                  }}
                  w="70px"
                >
                  {[50, 100, 200, 500, 750, 1000].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              </HStack>
              {parsedData.length > 0 && <Text>Durum: Aktif</Text>}
            </HStack>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
}

export default GelenDataModal;
